import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";

export const Route = createFileRoute("/print/$orderId/kitchen")({
  validateSearch: (raw: Record<string, unknown>) => ({
    tid: typeof raw.tid === "string" ? raw.tid : "",
  }),
  component: KitchenPrintPage,
  head: () => ({
    meta: [
      { title: "Comanda — Cocina" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type Selection = { addonProductId: string; name: string; cantidad: number; precioUnitario: number };
interface PrintItem {
  id: string; productName: string; tipoVenta: string;
  cantidad: number; unidadMedida: string;
  variantName: string | null; selections: Selection[];
}
interface PrintData {
  order: {
    id: string; customerName: string; customerPhone: string | null;
    status: string; metodEntrega: string; direccionEntrega: string | null;
    notes: string | null;
    fulfillmentMode?: string; scheduledFor?: string | null;
    kitchenPrintedAt: string | null; kitchenPrintCount: number;
    createdAt: string;
  };
  items: PrintItem[];
  tenant: { name: string; address: string | null; phone: string | null; logoUrl: string | null };
  location: { name: string; address: string | null } | null;
}

function shortId(id: string) { return id.slice(-6).toUpperCase(); }

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function KitchenPrintPage() {
  const { orderId } = Route.useParams();
  const { tid } = Route.useSearch();
  const [data, setData] = useState<PrintData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!tid) { setError("Tenant no especificado."); return; }
    apiGetJson<PrintData>(`/api/orders/print?id=${orderId}`, tid)
      .then(setData)
      .catch(() => setError("No se pudo cargar la orden."));
  }, [orderId, tid]);

  useEffect(() => {
    if (!data || !tid || recorded) return;
    apiSendJson(`/api/orders/print?id=${orderId}`, "POST", { type: "kitchen" }, tid)
      .catch(() => {/* no bloquear */})
      .finally(() => {
        setRecorded(true);
        setTimeout(() => window.print(), 300);
      });
  }, [data, tid, orderId, recorded]);

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-gray-500">Cargando comanda…</div>;
  }

  const { order, items, tenant, location } = data;
  const loc = location ?? { name: tenant.name, address: tenant.address };

  return (
    <>
      {/* ── Controles de pantalla (se ocultan al imprimir) ── */}
      <div className="no-print flex items-center justify-between gap-4 border-b bg-gray-50 px-4 py-3">
        <span className="text-sm font-medium text-gray-700">
          Comanda cocina — #{shortId(order.id)}
          {order.kitchenPrintCount > 0 && (
            <span className="ml-2 text-xs text-gray-400">
              ({order.kitchenPrintCount} impresión{order.kitchenPrintCount !== 1 ? "es" : ""} previas)
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded bg-gray-800 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-700"
        >
          Imprimir
        </button>
      </div>

      {/* ── Vista imprimible ── */}
      <div className="thermal-page">
        {/* Encabezado negocio */}
        <div className="thermal-header">
          <p className="thermal-business">{tenant.name}</p>
          {loc.address && <p className="thermal-meta">{loc.address}</p>}
        </div>

        <div className="thermal-divider" />

        {/* Tipo de ticket */}
        <p className="thermal-ticket-type">★ COMANDA COCINA ★</p>

        {/* Datos pedido */}
        <div className="thermal-section">
          <p><strong>Pedido #{shortId(order.id)}</strong></p>
          <p>{formatDate(order.createdAt)} {formatTime(order.createdAt)}</p>
          {order.fulfillmentMode === "scheduled" && order.scheduledFor && (
            <p><strong>PROGRAMADO:</strong> {formatDate(order.scheduledFor)} {formatTime(order.scheduledFor)}</p>
          )}
          <p>
            <strong>{order.metodEntrega === "domicilio" ? "DELIVERY" : "RETIRO EN LOCAL"}</strong>
            {order.metodEntrega === "domicilio" && order.direccionEntrega && (
              <>: {order.direccionEntrega}</>
            )}
          </p>
        </div>

        <div className="thermal-divider" />

        {/* Cliente */}
        <div className="thermal-section">
          <p><strong>Cliente:</strong> {order.customerName}</p>
          {order.customerPhone && <p><strong>Tel:</strong> {order.customerPhone}</p>}
        </div>

        <div className="thermal-divider" />

        {/* Ítems */}
        <div className="thermal-section">
          {items.map((item) => (
            <div key={item.id} className="thermal-item">
              <p className="thermal-item-name">
                <strong>
                  {item.tipoVenta === "peso"
                    ? `${item.cantidad} ${item.unidadMedida}`
                    : `${item.cantidad}x`}{" "}
                  {item.productName}
                </strong>
              </p>
              {item.variantName && (
                <p className="thermal-item-variant">  — {item.variantName}</p>
              )}
              {item.selections.map((s) => (
                <p key={s.addonProductId} className="thermal-item-addon">
                  + {s.cantidad > 1 ? `${s.cantidad}x ` : ""}{s.name}
                </p>
              ))}
            </div>
          ))}
        </div>

        {/* Notas */}
        {order.notes && (
          <>
            <div className="thermal-divider" />
            <div className="thermal-section">
              <p className="thermal-notes-label">NOTAS:</p>
              <p className="thermal-notes">{order.notes}</p>
            </div>
          </>
        )}

        <div className="thermal-divider-double" />
        <p className="thermal-footer">— Structura Market —</p>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .thermal-page {
          font-family: 'Courier New', Courier, monospace;
          font-size: 12px;
          line-height: 1.4;
          max-width: 300px;
          margin: 0 auto;
          padding: 8px 4px;
          background: white;
          color: black;
        }
        .thermal-header { text-align: center; margin-bottom: 4px; }
        .thermal-business { font-size: 14px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
        .thermal-meta { font-size: 10px; color: #333; }
        .thermal-ticket-type { text-align: center; font-size: 13px; font-weight: bold; margin: 4px 0; letter-spacing: 1px; }
        .thermal-divider { border-top: 1px dashed #000; margin: 6px 0; }
        .thermal-divider-double { border-top: 2px solid #000; margin: 8px 0; }
        .thermal-section { margin: 4px 0; }
        .thermal-section p { margin: 1px 0; }
        .thermal-item { margin: 4px 0; }
        .thermal-item-name { margin: 0; }
        .thermal-item-variant { margin: 0 0 0 8px; font-size: 11px; }
        .thermal-item-addon { margin: 0 0 0 12px; font-size: 11px; }
        .thermal-notes-label { font-weight: bold; text-transform: uppercase; font-size: 10px; margin-bottom: 2px; }
        .thermal-notes { font-style: italic; }
        .thermal-footer { text-align: center; font-size: 10px; color: #555; margin-top: 4px; }
      `}</style>
    </>
  );
}
